import { isComplete, type Draft } from "./draft";
import type { SessionStatus } from "@/lib/auth/session-store";

/** Every path the onboarding flow can send a person to. */
export const PATHS = {
  root: "/",
  welcome: "/welcome",
  date: "/start/date",
  time: "/start/time",
  place: "/start/place",
  reveal: "/start/reveal",
  consent: "/start/consent",
  signIn: "/start/sign-in",
  email: "/start/email",
  code: "/start/code",
  talk: "/start/talk",
  saving: "/start/saving",
  authCallback: "/auth/callback",
  today: "/today",
  voiceCall: "/talk/voice",
} as const;

export type ProfileState = "loading" | "none" | "present" | "error";

/**
 * Where the root sends a person, from what is known about them.
 *
 * Someone signed in with a chart lands on Today. Someone signed in without
 * one — an account created and abandoned, or a fresh sign-in whose details
 * are still in the draft — either saves the draft or is asked the first
 * question again. Nobody is parked on a spinner with nothing running.
 */
export function rootDestination(status: SessionStatus, profile: ProfileState, draft: Draft): string | null {
  if (status === "loading") return null;
  if (status === "signed_out") return PATHS.welcome;
  switch (profile) {
    case "loading":
      return null;
    case "error":
      return null;
    case "present":
      return PATHS.today;
    case "none":
      return isComplete(draft) ? PATHS.saving : PATHS.date;
  }
}

/**
 * Where a just-verified sign-in lands. A new person with details in hand
 * carries on to the last screen of first run; anyone else goes through the
 * root, which reads their profile and decides.
 */
export function destinationAfterSignIn(draft: Draft): string {
  return isComplete(draft) ? PATHS.talk : PATHS.root;
}

/** The intake step a person must return to when a later step lacks its input. */
export function firstUnansweredStep(draft: Draft): string | null {
  if (draft.date === null) return PATHS.date;
  if (draft.minutes === null && draft.accuracy !== "unknown") return PATHS.time;
  if (draft.place === null) return PATHS.place;
  return null;
}
